import { isNova } from '@/config'

/**
 * `GET /orders/:dynamoId` is one route with two implementations, chosen by the environment on the
 * backend, so its inferred response type is a union of both shapes. This establishes which one
 * arrived.
 *
 * It checks and throws rather than narrowing on the data alone. Narrowing alone would silently take
 * the mainline path when a Nova response was malformed, and the real fault would surface later as
 * some component finding a field missing. The environment states what should be true, so a mismatch
 * is an error rather than a branch. A cast would be worse again: it fails at first access with a
 * message that names neither the cause nor the expectation.
 */

/** The Nova shape nests each leg; the mainline shape has items and boxes at the top level. */
type Coupled = { legs: unknown }

export function isCoupledOrderDetails<T extends object>(result: T): result is T & Coupled {
  return 'legs' in result
}

/**
 * The mainline shape, or a thrown error saying why it is not.
 *
 * The Nova component tree is not ported yet, so a Nova environment throws here rather than
 * rendering the mainline tree against a coupled response.
 */
export function mainlineOrderDetails<T extends object>(result: T): Exclude<T, Coupled> {
  if (isCoupledOrderDetails(result)) {
    throw new Error(
      isNova
        ? 'Order details: the Nova component tree is not available yet.'
        : 'Order details: received a coupled Nova response on a mainline environment.',
    )
  }

  if (isNova) {
    throw new Error(
      'Order details: this is a Nova environment, so a coupled response was expected here.',
    )
  }

  return result as Exclude<T, Coupled>
}
